import React from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../../layout/pageLayout";
import { DesktopKpiStrip } from "../../shared/DesktopKpiStrip";
import { DeskEmpty, DeskPanel, DeskPill, DeskRowMenu } from "../../desktop/DeskTable";
import { formatEstimateStatus } from "../../../helpers/estimates";
import { formatCurrency } from "../../../helpers/estimateTotals";
import { estimateStatusTone } from "./estimateDesktopUi";
import { LocationsFeaturesPanel } from "./LocationsFeaturesPanel";
import { PricingReceipt } from "./PricingReceipt";
import "./estimateDesktop.css";

export function DeskLine({ label, value, emphasis = false, indent = false }) {
  return (
    <div className={`est-d-line${emphasis ? " is-emph" : ""}${indent ? " is-indent" : ""}`}>
      <span>{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}

/**
 * Desktop Estimate Summary. Phones keep EstimateSummaryScreen's own layout.
 * Main column = Locations & Features plus one Pricing receipt. Side column =
 * identity and the grand total.
 */
export function EstimateSummaryDesktop({
  title,
  clientName,
  estimateNumber,
  status,
  updatedLabel,
  onBack,
  statusControl,
  actions,
  archiveItem,
  onArchive,
  financials,
  taxSummary,
  liveAdjustedTotal,
  visibleLocations,
  estimateTree,
  materialsCatalog,
  onEditFeature,
  pricingSchemaMissing,
  pricing,
  canSavePricing,
  saving,
  onSavePricing,
  linkedProject,
  children,
}) {

  return (
    <CrewPilotModuleShell
      title={title}
      subtitle={clientName}
      onBack={onBack}
      desktopBackLabel="Estimates"
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          {actions}
          {archiveItem ? (
            <DeskRowMenu label="Estimate actions" items={[archiveItem]} onSelect={onArchive} />
          ) : null}
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Estimate totals"
        className="est-d-kpi4"
        tiles={[
          { key: "mat", value: formatCurrency(financials.calculatedMaterialsTotal), label: "Materials", tone: "blue" },
          { key: "lab", value: formatCurrency(financials.calculatedLabourTotal), label: "Labour", tone: "muted" },
          { key: "calc", value: formatCurrency(financials.calculatedEstimateTotal), label: "Calculated Total", tone: "gold" },
          { key: "grand", value: formatCurrency(taxSummary.grandTotal), label: "Grand Total", tone: "green" },
        ]}
      />

      <div className="dp-grid dp-grid--main-side">
        <div className="est-d-stack">
          <DeskPanel title="Locations & Features" className="dp--flush">
            {visibleLocations.length === 0 ? (
              <DeskEmpty title="No locations yet">Add locations from Edit Estimate.</DeskEmpty>
            ) : (
              <LocationsFeaturesPanel
                locations={visibleLocations}
                estimateTree={estimateTree}
                materialsCatalog={materialsCatalog}
                onEditFeature={onEditFeature}
              />
            )}
          </DeskPanel>

          <DeskPanel title="Pricing" className="est-pricing-panel">
            <PricingReceipt
              disabled={!canSavePricing || pricingSchemaMissing}
              schemaMissing={pricingSchemaMissing}
              subtotal={financials.calculatedEstimateTotal}
              waste={pricing.waste}
              travel={pricing.travel}
              misc={pricing.misc}
              onWaste={pricing.onWaste}
              onTravel={pricing.onTravel}
              onMisc={pricing.onMisc}
              wasteTaxable={pricing.wasteTaxable}
              travelTaxable={pricing.travelTaxable}
              miscTaxable={pricing.miscTaxable}
              onWasteTaxable={pricing.onWasteTaxable}
              onTravelTaxable={pricing.onTravelTaxable}
              onMiscTaxable={pricing.onMiscTaxable}
              adjustmentMode={pricing.adjustmentMode}
              adjustmentValue={pricing.adjustmentValue}
              onAdjustment={pricing.onAdjustment}
              beforeTax={pricing.beforeTax}
              finalPrice={pricing.finalPrice}
              onFinalPrice={pricing.onFinalPrice}
              adjusted={liveAdjustedTotal}
              taxBase={taxSummary.subtotal}
              tax1={pricing.tax1}
              tax2={pricing.tax2}
              onTax1={pricing.onTax1}
              onTax2={pricing.onTax2}
              grandTotal={taxSummary.grandTotal}
              canSave={canSavePricing}
              saving={saving}
              onSave={onSavePricing}
            />
          </DeskPanel>
        </div>

        <aside className="est-d-side" aria-label="Estimate details">
          <DeskPanel title="Estimate">
            <div className="est-d-id">
              <span>{estimateNumber}</span>
              <DeskPill tone={estimateStatusTone(status)}>{formatEstimateStatus(status)}</DeskPill>
            </div>
            <dl className="dp-kv dp-kv--tight">
              <dt>Client</dt>
              <dd>{clientName}</dd>
              <dt>Updated</dt>
              <dd>{updatedLabel}</dd>
              {linkedProject ? (
                <>
                  <dt>Project</dt>
                  <dd>{linkedProject}</dd>
                </>
              ) : null}
            </dl>
            {statusControl ? <div style={{ marginTop: 14 }}>{statusControl}</div> : null}
          </DeskPanel>

          <DeskPanel title="Total">
            <DeskLine label="Grand Total" value={taxSummary.grandTotal} emphasis />
          </DeskPanel>
        </aside>
      </div>
      {children}
    </CrewPilotModuleShell>
  );
}
